"use client";
import { usePowerSearch } from "../contexts/power-search-context";
import MultiSelectWithSearch from "../MultiSelectWithSearch";
import Button from "react-bootstrap/Button";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { mixpanelTrack } from "shared/trackEvents/mixpanelTracking";
import { isOpenRemunerationPlan } from "shared/plans";
import {
  POWER_SEARCH_EVENT_NAMES,
  MIXPANEL_PROPS,
} from "shared/trackEvents/constants";

const MultiSelectFieldWithSelectAll = ({
  jobTitleOptionsList,
  filterKey,
  title,
  allJobTitlesType,
  allJobTitlesSubtype,
  currentFilterValue,
  all_job_titles,
  updateFilter,
  updateAllJobTitlesToggle,
}) => {
  const currentUser = useSelector((state) => state.users.user);

  if (!jobTitleOptionsList || jobTitleOptionsList.length === 0) return null;

  const options = jobTitleOptionsList.map((opt) => ({
    label: opt,
    value: opt,
  }));

  const handleSelectAll = useCallback(() => {
    const currentAllState =
      all_job_titles[allJobTitlesType]?.[allJobTitlesSubtype];
    updateAllJobTitlesToggle(
      allJobTitlesType,
      allJobTitlesSubtype,
      !currentAllState
    );
    updateFilter(filterKey, !currentAllState ? options : []);
  }, [
    allJobTitlesType,
    allJobTitlesSubtype,
    all_job_titles,
    updateAllJobTitlesToggle,
    updateFilter,
    filterKey,
    options,
  ]);

  const handleChange = useCallback(
    (value) => {
      updateFilter(filterKey, value);
      if (
        value.length === 0 &&
        all_job_titles[allJobTitlesType]?.[allJobTitlesSubtype]
      ) {
        updateAllJobTitlesToggle(allJobTitlesType, allJobTitlesSubtype, false);
      }

      // Track role filter selection
      if (value.length > 0) {
        const eventName = isOpenRemunerationPlan(currentUser)
          ? POWER_SEARCH_EVENT_NAMES.OR_POWER_SEARCH_FILTER_ROLE_CLICKED
          : POWER_SEARCH_EVENT_NAMES.OD_POWER_SEARCH_FILTER_ROLE_CLICKED;

        mixpanelTrack(eventName, {
          [MIXPANEL_PROPS.FILTER_OPTION]: value[value.length - 1],
          [MIXPANEL_PROPS.FILTER_CATEGORY]: title,
          userEmail: currentUser?.email,
        });
      }
    },
    [updateFilter, filterKey, currentUser]
  );

  return (
    <div
      className="individual-filter mt-2"
      style={{ marginBottom: "10px", paddingBottom: "16px" }}
    >
      <div className="d-flex justify-content-between align-items-center">
        <p
          className="mb-0"
          style={{ fontSize: "14px", fontWeight: "500", color: "#AF6A3A" }}
        >
          {title}
        </p>
        <Button variant="link" onClick={handleSelectAll} className="small p-0">
          {!all_job_titles[allJobTitlesType]?.[allJobTitlesSubtype]
            ? "Select all"
            : "Unselect all"}
        </Button>
      </div>
      <MultiSelectWithSearch
        options={options}
        value={currentFilterValue}
        onChange={handleChange}
        placeholder={`Select ${title}`}
        className={"form-input debounce-search-input mt-1"}
      />
    </div>
  );
};

const JobTitlesFilter = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    filterOptions,
    sourceTypes,
    companyJobTitles,
    majorCompanyJobTitles,
    minorCompanyJobTitles,
    superannuationJobTitles,
    majorSuperannuationJobTitles,
    minorSuperannuationJobTitles,
    governmentJobTitles,
    majorGovernmentJobTitles,
    minorGovernmentJobTitles,
    otherGovernmentJobTitles,
    majorOtherGovernmentJobTitles,
    minorOtherGovernmentJobTitles,
    nfpJobTitles,
    majorNfpJobTitles,
    minorNfpJobTitles,
    all_job_titles,
    updateFilter,
    updateAllJobTitlesToggle,
  } = usePowerSearch();

  if (sourceTypes.length === 0) return null;

  return (
    <div className="sector-filters-container">
      <div
        className="d-flex align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <Button
          variant="link"
          size="sm"
          style={{
            display: "inline-block",
            padding: 0,
            margin: 0,
            textDecoration: "none",
            marginRight: "4px",
            focus: "none",
            outline: "none",
            border: "none",
            boxShadow: "none",
            fontWeight: "bold",
          }}
        >
          {isExpanded ? "−" : "+"}
        </Button>
        <p
          style={{
            fontSize: "14px",
            fontWeight: "bold",
            margin: 0,
          }}
        >
          ROLE
        </p>
      </div>
      <div className={`${isExpanded ? "d-block" : "d-none"}`}>
        {(sourceTypes.includes("company") ||
          sourceTypes.includes("private_company")) && (
          <>
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.director_positions || []
              }
              filterKey="companyJobTitles"
              title="Board Roles"
              allJobTitlesType="company"
              allJobTitlesSubtype="director_positions"
              currentFilterValue={companyJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.major || []
              }
              filterKey="majorCompanyJobTitles"
              title="Senior Company Roles"
              allJobTitlesType="company"
              allJobTitlesSubtype="majorCompany"
              currentFilterValue={majorCompanyJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.minor || []
              }
              filterKey="minorCompanyJobTitles"
              title="Other Company Roles"
              allJobTitlesType="company"
              allJobTitlesSubtype="minorCompany"
              currentFilterValue={minorCompanyJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
          </>
        )}
        {sourceTypes.includes("superannuation") && (
          <>
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list
                  ?.superannuation_director_positions || []
              }
              filterKey="superannuationJobTitles"
              title="Super Board Roles"
              allJobTitlesType="superannuation"
              allJobTitlesSubtype="director_positions"
              currentFilterValue={superannuationJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.major || []
              }
              filterKey="majorSuperannuationJobTitles"
              title="Senior Super Roles"
              allJobTitlesType="superannuation"
              allJobTitlesSubtype="majorSuperannuation"
              currentFilterValue={majorSuperannuationJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.minor || []
              }
              filterKey="minorSuperannuationJobTitles"
              title="Other Super Roles"
              allJobTitlesType="superannuation"
              allJobTitlesSubtype="minorSuperannuation"
              currentFilterValue={minorSuperannuationJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
          </>
        )}
        {sourceTypes.includes("government") && (
          <>
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.government_director_positions ||
                []
              }
              filterKey="governmentJobTitles"
              title="Govt Board Roles"
              allJobTitlesType="government"
              allJobTitlesSubtype="director_positions"
              currentFilterValue={governmentJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.major || []
              }
              filterKey="majorGovernmentJobTitles"
              title="Senior Govt Roles"
              allJobTitlesType="government"
              allJobTitlesSubtype="majorGovernment"
              currentFilterValue={majorGovernmentJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.minor || []
              }
              filterKey="minorGovernmentJobTitles"
              title="Other Govt Roles"
              allJobTitlesType="government"
              allJobTitlesSubtype="minorGovernment"
              currentFilterValue={minorGovernmentJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
          </>
        )}
        {sourceTypes.includes("other_government") && (
          <>
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list
                  ?.other_government_director_positions || []
              }
              filterKey="otherGovernmentJobTitles"
              title="Other Govt Board Roles"
              allJobTitlesType="other_government"
              allJobTitlesSubtype="director_positions"
              currentFilterValue={otherGovernmentJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.major || []
              }
              filterKey="majorOtherGovernmentJobTitles"
              title="Senior Other Govt Roles"
              allJobTitlesType="other_government"
              allJobTitlesSubtype="majorOtherGovernment"
              currentFilterValue={majorOtherGovernmentJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.minor || []
              }
              filterKey="minorOtherGovernmentJobTitles"
              title="Other Other Govt Roles"
              allJobTitlesType="other_government"
              allJobTitlesSubtype="minorOtherGovernment"
              currentFilterValue={minorOtherGovernmentJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
          </>
        )}
        {sourceTypes.includes("nfp") && (
          <>
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.nfp_director_positions || []
              }
              filterKey="nfpJobTitles"
              title="NFP Board Roles"
              allJobTitlesType="nfp"
              allJobTitlesSubtype="director_positions"
              currentFilterValue={nfpJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.major || []
              }
              filterKey="majorNfpJobTitles"
              title="Senior NFP Roles"
              allJobTitlesType="nfp"
              allJobTitlesSubtype="majorNfp"
              currentFilterValue={majorNfpJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
            <MultiSelectFieldWithSelectAll
              jobTitleOptionsList={
                filterOptions.job_titles_list?.csuite_positions?.minor || []
              }
              filterKey="minorNfpJobTitles"
              title="Other NFP Roles"
              allJobTitlesType="nfp"
              allJobTitlesSubtype="minorNfp"
              currentFilterValue={minorNfpJobTitles}
              all_job_titles={all_job_titles}
              updateFilter={updateFilter}
              updateAllJobTitlesToggle={updateAllJobTitlesToggle}
            />
          </>
        )}
      </div>
    </div>
  );
};
export default JobTitlesFilter;
